import { PureComponent, type ReactNode } from "react";
import Modal from "./Modal";
import Calendar from "./Calendar";
import { IconClickable, IconClickableType } from "./icon/IconClickable";
import { IconType } from "./icon/IconType";
import { IconSize } from "./icon/IconSize";
import dayjs from "dayjs";
import { type WithTranslation, withTranslation as withTranslationBase } from "react-i18next";
import { SimpleSelect } from "./Select/SimpleSelect";
import { getLastValidDate } from "~/common/utils/dateUtils";
import { Button, Stack } from "~/common/mui";

interface SelectDatePopupProps extends WithTranslation {
  date: Date | null;

  onClose: () => void;
  onSelect?: (newDate: Date) => void;
  isDayDisabled?: (date: Date) => boolean;
  minYear?: number;
  maxYear?: number;
}

interface SelectDatePopupState {
  selectedDate: Date | null;
  monthDate: Date;
  originalYear: number;
  months: {
    label: ReactNode;
    value: number;
  }[];
}

const MONTH_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((val) => `month.${val}`);

class SelectDatePopup extends PureComponent<SelectDatePopupProps, SelectDatePopupState> {
  public static defaultProps: Partial<SelectDatePopupProps> = {
    date: new Date(),
  };

  previousMonth = () =>
    this.setState((previousState) => ({
      monthDate: dayjs(previousState.monthDate).add(-1, "month").toDate(),
    }));

  nextMonth = () =>
    this.setState((previousState) => ({
      monthDate: dayjs(previousState.monthDate).add(1, "month").toDate(),
    }));

  setMonth = (val: number = 1) => {
    return this.setState((previousState) => ({
      monthDate: dayjs(previousState.monthDate).set("month", +val).toDate(),
    }));
  };

  setYear = (val: number = 1990) => {
    return this.setState((previousState) => ({
      monthDate: dayjs(previousState.monthDate).set("year", +val).toDate(),
    }));
  };

  selectDate = () => {
    if (this.props.onSelect && this.state.selectedDate) {
      this.props.onSelect(this.state.selectedDate);
    }
    this.props.onClose();
  };

  constructor(props: SelectDatePopupProps) {
    super(props);

    const monthDate = props.date ? props.date : new Date();

    const t = props.t;
    const months = MONTH_KEYS.map((key) => t(key)).map((val, index) => ({
      label: val,
      value: index,
    }));

    const originalYear = Math.min(new Date().getFullYear(), monthDate.getFullYear());

    this.state = {
      selectedDate: props.date,
      originalYear,
      monthDate,
      months,
    };
  }

  render() {
    const { onClose, isDayDisabled, t } = this.props;
    const { monthDate, selectedDate, originalYear, months } = this.state;

    const years = [];
    const minYear = this.props.minYear !== undefined ? this.props.minYear : originalYear;
    let maxYear;
    if (this.props.maxYear) {
      maxYear = this.props.maxYear;
    } else {
      maxYear = getLastValidDate().getFullYear();
    }

    for (let i = minYear; i <= maxYear; i++) {
      years.push({ label: i + "", value: i });
    }

    const isPrevMonthDisabled = monthDate.getFullYear() === minYear && monthDate.getMonth() === 0;
    const isNextMonthDisabled = monthDate.getFullYear() === maxYear && monthDate.getMonth() === 11;

    return (
      <Modal
        onClose={onClose}
        footerChildren={
          <Stack direction={"row"} gap={1}>
            <Button data-test-id={"date-selector-btn-cancel"} onClick={onClose} variant={"text"}>
              {t("orderDetailsPage.date.selector.cancel")}
            </Button>
            <Button data-test-id={"date-selector-btn-confirm"} onClick={this.selectDate} disabled={!selectedDate}>
              {t("orderDetailsPage.date.selector.confirm")}
            </Button>
          </Stack>
        }
      >
        <div className="scha__change-date__controls">
          <IconClickable
            type={IconType.ChevronLeft}
            onIconClick={this.previousMonth}
            size={IconSize.FONT_20}
            className="scha__change-date__icon"
            clickableType={IconClickableType.PRIMARY}
            disabled={isPrevMonthDisabled}
            data-test-id={"date-selector-btn-prev-month"}
          />
          <SimpleSelect
            className={"scha__change-date__years"}
            data-test-id={"date-selector-select-year"}
            values={years}
            value={monthDate.getFullYear()}
            onChange={(e) => this.setYear(+e.target.value)}
          />
          <SimpleSelect
            className={"scha__change-date__months"}
            data-test-id={"date-selector-select-month"}
            values={months}
            value={monthDate.getMonth()}
            onChange={(e) => this.setMonth(+e.target.value)}
          />
          <IconClickable
            onIconClick={this.nextMonth}
            type={IconType.ChevronRight}
            size={IconSize.FONT_20}
            className="scha__change-date__icon"
            clickableType={IconClickableType.PRIMARY}
            disabled={isNextMonthDisabled}
            data-test-id={"date-selector-btn-next-month"}
          />
        </div>
        <Calendar
          className="scha__change-date__calendar"
          visibleMonth={monthDate}
          onClick={(newDate) => this.setState({ selectedDate: newDate })}
          selectedDate={selectedDate}
          isDayDisabled={isDayDisabled}
        />
      </Modal>
    );
  }
}

export default withTranslationBase()(SelectDatePopup);
